import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Descriptions,
  Modal,
  Select,
  Input,
  message,
  Tooltip,
  Spin,
  Alert,
  Tabs,
  Statistic,
  Row,
  Col,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilePdfOutlined,
  BankOutlined,
  DollarOutlined,
  TransactionOutlined,
} from "@ant-design/icons";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import Header from "../../../../Componets/Header";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";
import headers from "../../../../Data/Tables/Accounting/BankReconcilliation/headers";
import {
  auto_reconcile,
  get_bank_statement,
  manual_reconcile,
  unreconcile_transaction,
} from "../../../../Controllers/Accounting/BankReconcilliation";
import { get_all_manual_journals } from "../../../../Controllers/Accounting/Services/manualJournalsService";

const { TextArea } = Input;

const BankStatementView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [reconcileModalVisible, setReconcileModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedJournalId, setSelectedJournalId] = useState(null);
  const [reconcileNotes, setReconcileNotes] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [autoReconcileLoading, setAutoReconcileLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("unreconciled");
  const [header, setHeader] = useState(headers);

  useEffect(() => {
    fetchStatement();
    fetchJournalEntries();
  }, [id]);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("user");
      const response = await get_bank_statement(id);
      if (response.success) {
        setStatement(response.data);
      } else {
        toast.error(response.message || "Failed to fetch statement");
      }
    } catch (error) {
      toast.error("Failed to fetch bank statement");
    } finally {
      setLoading(false);
    }
  };

  const fetchJournalEntries = async () => {
    try {
      const token = localStorage.getItem("user");
      const params = { limit: 100 };
      const response = await get_all_manual_journals(params);
      if (response.success) {
        setJournalEntries(response.data);
      }
    } catch (error) {
      console.error("Error fetching journal entries:", error);
    }
  };

  const handleManualReconcile = async () => {
    if (!selectedJournalId) {
      toast.error("Please select a journal entry to reconcile");
      return;
    }

    setReconciling(true);
    try {
      const token = localStorage.getItem("user");
      const body = {
        journalEntryId: selectedJournalId,
        notes: reconcileNotes,
      };
      const response = await manual_reconcile(body);

      if (response.data?.success) {
        toast.success("Transaction reconciled successfully");
        setReconcileModalVisible(false);
        setSelectedTransaction(null);
        setSelectedJournalId(null);
        setReconcileNotes("");
        fetchStatement();
      } else {
        toast.error(response.data?.message || "Reconciliation failed");
      }
    } catch (error) {
      toast.error("Failed to reconcile transaction");
    } finally {
      setReconciling(false);
    }
  };

  const handleUnreconcile = async (transactionId) => {
    Modal.confirm({
      title: "Unreconcile Transaction",
      content: "Are you sure you want to unreconcile this transaction?",
      onOk: async () => {
        try {
          const token = localStorage.getItem("user");
          await unreconcile_transaction();
          toast.success("Transaction unreconciled successfully");
          fetchStatement();
        } catch (error) {
          toast.error("Failed to unreconcile transaction");
        }
      },
    });
  };

  const handleAutoReconcile = async () => {
    setAutoReconcileLoading(true);
    try {
      const token = localStorage.getItem("user");
      const response = await auto_reconcile(id);

      if (response?.success) {
        toast.success(
          `Auto-reconciliation completed: ${response.data.matchedCount} matched, ${response.data.unmatchedCount} unmatched`,
        );
        fetchStatement();
      } else {
        toast.error(response?.message || "Auto-reconciliation failed");
      }
    } catch (error) {
      toast.error("Auto-reconciliation failed");
    } finally {
      setAutoReconcileLoading(false);
    }
  };

  const openReconcileModal = (transaction) => {
    setSelectedTransaction(transaction);
    setSelectedJournalId(null);
    setReconcileNotes("");
    setReconcileModalVisible(true);
  };

  const getTransactionColumns = (showReconciled = false) => {
    const baseColumns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 100,
        render: (date) => moment(date).format("DD/MM/YYYY"),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
      },
      {
        title: "Reference",
        dataIndex: "reference",
        key: "reference",
        width: 120,
      },
      {
        title: "Debit (Withdrawal)",
        dataIndex: "debit",
        key: "debit",
        width: 130,
        align: "right",
        render: (val) =>
          val > 0 ? (
            <span className="text-red-600 font-semibold">{val.toFixed(3)}</span>
          ) : (
            "-"
          ),
      },
      {
        title: "Credit (Deposit)",
        dataIndex: "credit",
        key: "credit",
        width: 130,
        align: "right",
        render: (val) =>
          val > 0 ? (
            <span className="text-green-600 font-semibold">
              {val.toFixed(3)}
            </span>
          ) : (
            "-"
          ),
      },
      {
        title: "Amount",
        key: "amount",
        width: 120,
        align: "right",
        render: (_, record) => {
          const amount = record.debit || record.credit;
          return <span className="font-mono">{amount?.toFixed(3)}</span>;
        },
      },
    ];

    if (showReconciled) {
      baseColumns.push({
        title: "Reconciled To",
        dataIndex: "reconciledTo",
        key: "reconciledTo",
        width: 150,
        render: (journalId, record) => {
          if (!journalId) return "-";
          return (
            <Tooltip
              title={
                record.reconciledDate &&
                `Reconciled on ${moment(record.reconciledDate).format("DD/MM/YYYY HH:mm")}`
              }
            >
              <Tag color="green">Journal Entry</Tag>
            </Tooltip>
          );
        },
      });
      baseColumns.push({
        title: "Reconciled Date",
        dataIndex: "reconciledDate",
        key: "reconciledDate",
        width: 120,
        render: (date) => (date ? moment(date).format("DD/MM/YYYY") : "-"),
      });
      baseColumns.push({
        title: "Actions",
        key: "actions",
        width: 100,
        render: (_, record) => (
          <Button
            size="small"
            danger
            onClick={() => handleUnreconcile(record._id)}
          >
            Unreconcile
          </Button>
        ),
      });
    } else {
      baseColumns.push({
        title: "Actions",
        key: "actions",
        width: 120,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => openReconcileModal(record)}
          >
            Reconcile
          </Button>
        ),
      });
    }

    return baseColumns;
  };

  const reconciledTransactions =
    statement?.transactions?.filter((t) => t.reconciled) || [];
  const unreconciledTransactions =
    statement?.transactions?.filter((t) => !t.reconciled) || [];

  const tabItems = [
    {
      key: "unreconciled",
      label: (
        <span>
          <CloseCircleOutlined className="mr-2 text-orange-500" />
          Unreconciled ({unreconciledTransactions.length})
        </span>
      ),
      children: (
        <Table
          columns={getTransactionColumns(false)}
          dataSource={unreconciledTransactions}
          rowKey="_id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: "reconciled",
      label: (
        <span>
          <CheckCircleOutlined className="mr-2 text-green-500" />
          Reconciled ({reconciledTransactions.length})
        </span>
      ),
      children: (
        <Table
          columns={getTransactionColumns(true)}
          dataSource={reconciledTransactions}
          rowKey="_id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="p-6">
        <Alert message="Bank statement not found" type="error" />
      </div>
    );
  }

  return (
    <Header headers={header} setHeaders={setHeader}>
      <ScrollWrapper>
        <div className="mb-6 flex justify-between items-center mt-2">
          <div>
            <div></div>
            <p className="text-gray-500 mt-1">{statement.statementId}</p>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchStatement}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleAutoReconcile}
              loading={autoReconcileLoading}
            >
              Auto Reconcile
            </Button>
            <Button onClick={() => navigate("/app/bank-reconciliation")}>
              Back to List
            </Button>
          </Space>
        </div>
        <Row gutter={16} className="mb-6">
          <Col span={6}>
            <Card>
              <Statistic
                title="Bank Account"
                value={statement.bankAccountId?.name || "-"}
                prefix={<BankOutlined />}
              />
              <div className="text-gray-400 text-sm mt-1">
                {statement.bankAccountId?.code}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Period"
                value={`${moment(statement.statementPeriodStart).format("DD/MM/YYYY")} - ${moment(statement.statementPeriodEnd).format("DD/MM/YYYY")}`}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Closing Balance"
                value={statement.closingBalance?.toFixed(3)}
                valueStyle={{
                  color: statement.closingBalance >= 0 ? "#3f8600" : "#cf1322",
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Reconciliation Status"
                value={statement.status?.toUpperCase()}
                valueStyle={{
                  color:
                    statement.status === "completed"
                      ? "#3f8600"
                      : statement.status === "in_progress"
                        ? "#1890ff"
                        : "#faad14",
                }}
              />
              <div className="text-sm text-gray-500 mt-1">
                {statement.summary?.reconciledCount || 0} /{" "}
                {statement.transactions?.length || 0} reconciled
              </div>
            </Card>
          </Col>
        </Row>
        <Card className="mb-6">
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Statement ID">
              {statement.statementId}
            </Descriptions.Item>
            <Descriptions.Item label="Statement Date">
              {moment(statement.statementDate).format("DD/MM/YYYY")}
            </Descriptions.Item>
            <Descriptions.Item label="Opening Balance">
              {statement.openingBalance?.toFixed(3)}
            </Descriptions.Item>
            <Descriptions.Item label="Closing Balance">
              {statement.closingBalance?.toFixed(3)}
            </Descriptions.Item>
            <Descriptions.Item label="File Name">
              {statement.fileName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Uploaded Date">
              {moment(statement.createdAt).format("DD/MM/YYYY HH:mm")}
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <Card className="mb-6">
          <Row gutter={16}>
            <Col span={8}>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {statement.summary?.totalDebits?.toFixed(3) || "0.000"}
                </div>
                <div className="text-gray-500">Total Debits (Withdrawals)</div>
              </div>
            </Col>
            <Col span={8}>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {statement.summary?.totalCredits?.toFixed(3) || "0.000"}
                </div>
                <div className="text-gray-500">Total Credits (Deposits)</div>
              </div>
            </Col>
            <Col span={8}>
              <div
                className={`text-center p-4 rounded-lg ${
                  Math.abs(statement.summary?.difference || 0) < 0.01
                    ? "bg-green-50"
                    : "bg-red-50"
                }`}
              >
                <div
                  className={`text-2xl font-bold ${
                    Math.abs(statement.summary?.difference || 0) < 0.01
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {statement.summary?.difference?.toFixed(3) || "0.000"}
                </div>
                <div className="text-gray-500">Difference</div>
              </div>
            </Col>
          </Row>
        </Card>
        <Card>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
          />
        </Card>
        <Modal
          title="Reconcile Transaction"
          open={reconcileModalVisible}
          onOk={handleManualReconcile}
          onCancel={() => {
            setReconcileModalVisible(false);
            setSelectedTransaction(null);
            setSelectedJournalId(null);
            setReconcileNotes("");
          }}
          confirmLoading={reconciling}
          width={600}
        >
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Transaction Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Date:</span>{" "}
                    {moment(selectedTransaction.date).format("DD/MM/YYYY")}
                  </div>
                  <div>
                    <span className="text-gray-500">Reference:</span>{" "}
                    {selectedTransaction.reference || "-"}
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500">Description:</span>{" "}
                    {selectedTransaction.description || "-"}
                  </div>
                  <div>
                    <span className="text-gray-500">Amount:</span>{" "}
                    <span className="font-bold">
                      {selectedTransaction.debit > 0
                        ? `Debit: ${selectedTransaction.debit.toFixed(3)}`
                        : `Credit: ${selectedTransaction.credit.toFixed(3)}`}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Journal Entry to Reconcile
                </label>
                <Select
                  className="w-full"
                  placeholder="Search and select journal entry"
                  showSearch
                  optionFilterProp="children"
                  value={selectedJournalId}
                  onChange={setSelectedJournalId}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {journalEntries.map((journal) => (
                    <Select.Option key={journal._id} value={journal._id}>
                      {journal.referenceNumber} -{" "}
                      {moment(journal.date).format("DD/MM/YYYY")} -{" "}
                      {journal.description?.substring(0, 50)}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Notes (Optional)
                </label>
                <TextArea
                  rows={3}
                  placeholder="Add reconciliation notes..."
                  value={reconcileNotes}
                  onChange={(e) => setReconcileNotes(e.target.value)}
                />
              </div>
            </div>
          )}
        </Modal>
      </ScrollWrapper>
    </Header>
  );
};

export default BankStatementView;
